<template>
  <div>
    <!-- Event status (Loading, Countdown) -->
    <EventStatusBoard v-if="eventStatusBoard.active" :screener="screener" @leaveScreening="$emit('leaveScreening')">
      <AppLoader
        v-if="eventStatusBoard.loading"
        :message="eventStatusBoard.message"
      />
      <ScreenerCountDown
        v-if="eventStatusBoard.countdown"
        :screener_date="screener.screening_starts_at"
        @finished="resolveEventManager()"
      />
      <VodAvailabilityMessage
        v-if="eventStatusBoard.vodFirstWatched"
        :screener="screener"
        :message="eventStatusBoard.message"
        @firstWatchInitiated="onFirstWatchInitiated"
      />
    </EventStatusBoard>

    <!-- Main Content (Stage content) -->
    <v-layout
      v-else-if="eventManager.state == DEFINES.EVENT_MANAGER_STATE.ACTIVE"
      row
      wrap
      fill-height
    >
      <v-flex
        xs12
        :md8="eventManager.liveChat.active || eventManager.multifilm.active"
        class="pa-2 pt-2"
      >
        <v-layout row wrap fill-height>
          <v-flex>
            <div>
              <component
                :is="eventManager.onStage.currentStream"
                :key="currentMovieIndex"
                :meta="eventManager.onStage.meta"
                :consumer="consumer"
                :previewMode="previewMode"
                @addToCal="$emit('addToCal')"
                @leaveScreening="$emit('leaveScreening')"
                @OnMovieIndexChange="OnMovieIndexChange"
              ></component>
            </div>
          </v-flex>
        </v-layout>
      </v-flex>

      <!-- Live chat, Playlist -->
      <v-flex
        v-if="eventManager.state == DEFINES.EVENT_MANAGER_STATE.ACTIVE"
        xs12
        :md4="eventManager.liveChat.active || eventManager.multifilm.active"
      >
        <v-layout column wrap>
          <v-flex v-if="eventManager.liveChat.active" class="pa-2 pt-2">
            <component
              :is="DEFINES.LiveChat"
              :stream="eventManager.liveChat.meta"
            ></component>
          </v-flex>
          <v-flex v-if="eventManager.multifilm.active" class="pa-2 pt-2">
            <MoviesPlaylist
              :currentIndex="currentMovieIndex"
              :screener="screener"
              :showTrailerBtn="false"
              @onMovieClick="onMovieClick"
            ></MoviesPlaylist>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
  </div>
</template>
<script>
import { mapState, mapGetters } from "vuex";
import EventStatusBoard from "@/components/screener/EventStatusBoard";
import LiveChat from "@/components/screener/Chat";
import MoviesPlaylist from "@/components/screener/FilmList";
import * as Streams from "../streams";
import AppLoader from "@/components/AppLoader";
import { EventBus } from "@/event-bus";
import DEFINES from "@/defines";
import ScreenerCountDown from "@/components/screener/CountDown";
import VodAvailabilityMessage from "../VodAvailabilityMsg.vue"
import i18n from "@/i18n"

const EVENT_MANAGER_STATE = DEFINES.EVENT_MANAGER_STATE;
export default {
  name: "EventManagerRoot",
  props: {
    screener: {
      type: Object,
      default: null,
    },
    company: {
      type: Object,
      default: null,
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
    isEmbedMode: {
      type: Boolean,
      default: false,
    },
    isFullScreenMode: {
      type: Boolean,
      default: false,
    },
    isLoggedInWithLink :{
      type: Boolean,
      default: false,
    }
  },
  computed: {
    ...mapState({
      consumer: (state) => state.consumer.profile,
    }),
    ...mapGetters("player", {
      getPlayerState: "getScreenerConfig",
    }),
  },
  data() {
    return {
      eventManager: {
        state: this.DEFINES.EVENT_MANAGER_STATE.INITIALIZING,
        onStage: {
          active: false,
          meta: {}
        },
        liveChat: {
          active: false,
          meta: {}
        },
        multifilm: {
          active: false,
          meta: {}
        },
      },
      eventStatusBoard: {
        active: true,
        loading: false,
        message: null,
        error: true,
        isResolved: false,
        countdown: false,
        vodFirstWatched: false
      },
      fb_whitelist_fields: [
        'streams',
        'screener_urls',
        'films'
      ],
      isMounted: false,
      currentMovieIndex: 0,
      discontinuedStreams:[this.DEFINES.ZoomMeeting, this.DEFINES.JitsiLiveStream]
    };
  },
  components: {
    EventStatusBoard,
    AppLoader,
    ...Streams,
    LiveChat,
    MoviesPlaylist,
    ScreenerCountDown,
    VodAvailabilityMessage
  },

  created() {
    this.initializeEventManager();
  },

  mounted() {
    this.resolveEventManager();
    this.resetPlayerState()
    if(this.isLoggedInWithLink && this.eventStatusBoard.countdown){
      this.$store.dispatch("app/showMessage", {
        message: i18n.t("screener.waiting_for_screening"),
        color: "success",
      });
    }
  },
  methods: {
    async initializeEventManager() {
      /**
       * 1. Update status of event manager
       * 2. Configure All the event busses
       */
      await this.updateEventManagerState(this.DEFINES.EVENT_MANAGER_STATE.INITIALIZING);
      await this.bindScreenerWithFB(this.DEFINES.EVENT_MANAGER_STATE.INITIALIZING);

    },

    /**
     * Here we'll take decision which component needs to be placed
     * with its active state
     */
    async resolveEventManager() {

      /**
       * RESOLVE VOD EVENTS: If event is VOD event then only resolve VOD items
       * 1. Resolve First Watch: Whether users initiated to watch or not
       * 2. Resolve ON-Stage: Which element need to placed on stage
       * 3. Resolve Playlist: Wether to show playlist or not
       */
      if (this.screener.is_vod_event) {
        return Promise.all([
          this.resolveFirstWatch(this.screener),
          this.resolveOnStage(this.screener.streams),
          this.resolvePlaylist(this.screener),
        ])
        .then((response) => {
          this.executeEventManager();
          console.log();
          this.updateEventManagerState(this.DEFINES.EVENT_MANAGER_STATE.ACTIVE);
          this.eventStatusBoard.loading = false;
        })
        .catch((error) => {
          this.updateEventManagerState(this.DEFINES.EVENT_MANAGER_STATE.ERROR);
        })
        .finally(() => {
          this.eventStatusBoard.loading = false;
        });
      }

      /**
       * RESOLVE STANDARD EVENTS: Resolve all elements if any one of resolver failed then
       * do not execute the elements
       */
      return Promise.all([
        this.resolveCountdown(this.screener),
        this.resolveOnStage(this.screener.streams),
        this.resolveLiveChat(this.screener.streams),
        this.resolvePlaylist(this.screener),
      ])
      .then((response) => {
        this.executeEventManager();
        this.updateEventManagerState(this.DEFINES.EVENT_MANAGER_STATE.ACTIVE);
        this.eventStatusBoard.loading = false;
      })
      .catch((error) => {
        this.updateEventManagerState(this.DEFINES.EVENT_MANAGER_STATE.ERROR);
      })
      .finally(() => {
        this.eventStatusBoard.loading = false;
      });
    },

    /**
     * IN CASE OF VOD: If first watch is not initiated yet then
     * show a message to users
     */
    resolveFirstWatch(screener){
      /**
       * BYPASS Checking for preview_mode
       */
      if(this.previewMode){
        return Promise.resolve();
      }
      if(screener.is_vod_event && !screener.is_event_started){
        this.eventStatusBoard.active = true;
        this.eventStatusBoard.vodFirstWatched = true;
        this.eventStatusBoard.message = i18n.t("vod.first.time.availability.msg", {hours: screener.vod_availability,});
        return Promise.reject();
      }
    },

    /**
     * If screening is not started yet then show counter with eventStatusBoard
     */
    async resolveCountdown(screener) {

      //BYPASS COUNTDOWN for preview users
      //FIXME:: Add countdown for small time in case of preview user
      if(this.previewMode){
        return Promise.resolve();
      }

      let screening_start_date = this.moment.utc(screener.screening_starts_at).local();
      let current_date = this.moment().local();
      let isScreeningTimeOccurred = this.moment(current_date).isAfter(screening_start_date);

      /**
       * If screening is not started then simply reject the promise and execution
       * will going to be rejected
       */
      if (!isScreeningTimeOccurred) {
        this.eventStatusBoard.active = true;
        this.eventStatusBoard.countdown = true;
        return Promise.reject();
      }

      this.eventStatusBoard.active = false;
      this.eventStatusBoard.countdown = false;
      return Promise.resolve();
    },

    /**
     * Resolve stage, here stage refers to the primary content (player, meetings, etc.)
     */
    resolveOnStage(streams) {

      if(streams){
        // Removing discontinued streams
        this.discontinuedStreams.forEach((streamKey) => {
          delete streams[streamKey];
        });
      }

      /**  Prepare meta data for relevant component  */
      let additionalMeta = {};
      additionalMeta.company_logo = this.company.logo_url;
      additionalMeta.screener_id = this.screener.id;

      // Add Embed Options
      additionalMeta.isEmbedMode = this.isEmbedMode
      additionalMeta.isFullScreenMode = this.isFullScreenMode

      let forced_stream = streams ? this.getForcedStream(streams) : null;
      /**
       * 1. If any type of streams are not by default MovieStream will be forced
       */
      if (!forced_stream) {
        this.eventManager.onStage.currentStream = this.DEFINES.MovieStream;
        this.eventManager.onStage.meta = {...this.screener, ...additionalMeta};
        return
      }

      /**
       * 2. If any stream is already forced then use the forced streams only
       */
      this.eventManager.onStage.currentStream = forced_stream.type;
      this.eventManager.onStage.meta = {...forced_stream, ...additionalMeta, ...this.screener};
      // this.$store.dispatch("app/showSupportNotification", {
      //   screener_id: this.screener.id,
      //   message: forced_stream.title + i18n.t("screener.meeting.is_about_to_stream")
      // });
      return

    },

    /**
     * Resolve Chat
     */
    resolveLiveChat(streams) {
      /**
       * Do not resolve chat if event is VOD as we are not
       * allowing chats for VOD events
       */
      if (streams && streams[this.DEFINES.LiveChat]) {
        this.eventManager.liveChat.meta = streams[this.DEFINES.LiveChat];
        return
      }

      this.eventManager.liveChat.meta = null;
      return ;
    },

    /**
     * Resolve Multifilm/Playlist
     */
    resolvePlaylist(screener) {
      this.eventManager.multifilm.meta = {
        films: screener.films,
      };
      return
    },

    /**
     * Update event manager state with ongoing operations
     */
    updateEventManagerState(state, message = null) {
      this.eventManager.state = state;

      if (state == this.DEFINES.EVENT_MANAGER_STATE.INITIALIZING) {
        this.eventStatusBoard.active = true;
        this.eventStatusBoard.loading = true;
        this.eventStatusBoard.message = "Please wait while we are fetching your screening";
        return;
      }

      // if (state == this.DEFINES.EVENT_MANAGER_STATE.INTERRUPTED) {
      //   this.eventStatusBoard.active = true;
      //   this.eventStatusBoard.loading = true;
      //   this.eventStatusBoard.message =
      //     "Screening Is Interre";
      //   return;
      // }

      if (state == this.DEFINES.EVENT_MANAGER_STATE.ACTIVE) {
        this.eventStatusBoard.active = false;
        this.eventStatusBoard.loading = false;
        this.eventStatusBoard.message = null;
        return;
      }

      if (state == this.DEFINES.EVENT_MANAGER_STATE.ERROR) {
        this.eventStatusBoard.active = true;
        this.eventStatusBoard.loading = false;
        return;
      }
    },

    /**
     * Get which stream is forced
     */
    getForcedStream(streams) {
      let available_streams = Object.entries(streams);
      let forced_stream = null;

      available_streams.forEach((element) => {
        var stream = element[1];
        if (stream.force) {
          forced_stream = stream;
        }
      });
      return forced_stream ? forced_stream : null;
    },

    /**
     * Bind screener with Firebase so all the new updates can be listen here
     */
    async bindScreenerWithFB() {
      let _this = this;
      this.firebase.database().ref(`screener/${this.screener.id}/`).on('value',function(snapshot) {

          let updated_screener = snapshot.val();
          if(_this.isMounted){
            _this.screener['streams']= updated_screener['streams'];
            _this.resolveEventManager();
          }else {
            _this.isMounted = true;
          }
        })
    },

    /**
     * Check wether a stream is active or not
     */
    isStreamActive(stream) {
      return (
        this.screener.streams != null &&
        this.screener.streams[stream] != undefined &&
        this.screener.streams[stream].active
      );
    },

    // Execution of event manager
    executeEventManager() {
      /**
       * 1. Execute onStage Item
       */
      if (this.eventManager.onStage) {
        this.eventManager.onStage.active = true;
      }

      /**
       * 1. Execute onStage Item
       */
      if (
        this.eventManager.liveChat &&
        this.isStreamActive(this.DEFINES.LiveChat) &&
        (!this.screener.is_vod_event)
      ) {
        this.eventManager.liveChat.active = true;
      }else {
        this.eventManager.liveChat.active = false;
      }

      /**
       * 1. Execute Playlist Item
       */
      if (
          this.eventManager.multifilm &&
          this.screener.films.length > 1 &&
          this.eventManager.onStage.currentStream == this.DEFINES.MovieStream
      ) {
            this.eventManager.multifilm.active = true;
      } else {
            this.eventManager.multifilm.active = false;
      }
    },

    /**
     * VOD FIRST WATCH: Get fresh data from server after first watch initiated
     */
    onFirstWatchInitiated() {
      this.updateScreener();
      this.resolveEventManager();
    },

    async updateScreener() {
      await this.$store
        .dispatch("screening/getWithoutEnforce", {
          id: this.$route.params.screener_id,
        })
        .then((response) => {
          this.screener = response.screener;
           this.$emit("update:screener",this.screener)
        })
        .catch((response) => {
          this.$store.dispatch("app/showMessage", {
            message: error.message,
            color: "error",
          });
        });
    },

    // Playlist Switcher controller
    async onMovieClick(index) {
      // Allow switching movies only if admin allowed
      const isSwitchingAllowed = this.screener.event_configuration && this.screener.event_configuration.movie_switching_enabled
      if(!isSwitchingAllowed){
        return
      }


      await this.$store
        .dispatch("player/updateMovieIndex", {
          screener_id: this.screener.id,
          currentMovieIndex: index,
        })
        .then(async (response) => {
          await this.$store.dispatch("player/updateCurrentTime", {
            screener_id: this.screener.id,
            currentTime: 0,
          });
          this.currentMovieIndex = index;
        });
    },

    async resetPlayerState() {
      /**
       * Reset Player state only for preview mode
       */
      if(!this.previewMode){
        const playerState = this.getPlayerState(this.screener.id)
        this.currentMovieIndex = playerState ? playerState.currentMovieIndex : 0
        return
      }

      let default_state             = this.DEFINES.PLAYER_CONFIG.SCREENER;
      default_state.screener_id     = this.screener.id;
      await this.$store.dispatch("player/updateScreenerConfig",default_state)
      return
    },

    OnMovieIndexChange(index){
      this.currentMovieIndex = index;
    }
  },
};
</script>
